// src/pages/settings/settings/BrandingConfiguration.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, ColorPicker, Input, Space, Spin, Typography, Upload } from "antd";
import { DeleteOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";

import { SectionCard } from "../../../components/shared";
import XoeMedLogo from "../../../components/branding/XoeMedLogo";
import { useBranding } from "../../../context/BrandingContext";
import { useNotifications } from "../../../context/NotificationContext";
import { extractValidationErrors } from "../../../utils/apiError";
import { getReadableTextColor } from "../../../utils/colorUtils";
import { checkBrandColorContrast, formatRatio, WCAG_AA_NORMAL } from "../../../utils/branding/contrast";
import {
	detectImageFormat,
	EXTENSION_FORMATS,
	extensionFormat,
	FORMAT_DESCRIPTION,
	FORMAT_LABEL,
	ImageFormat,
	isAcceptedFormat,
} from "../../../utils/branding/imageSignature";
import {
	BrandingAdmin,
	BrandingAsset,
	BrandingAssetVariant,
	deleteBrandingAsset,
	fetchBrandingAdmin,
	updateBranding,
	uploadBrandingAsset,
	UpdateBrandingPayload,
} from "../../../api/admin/brandingAdminApi";

const { Text, Paragraph } = Typography;

/**
 * S9 (ClickUp 86d3w1g8u) — the clinic's Branding tab.
 *
 * Registered in `src/components/settings/settingsTabs.tsx`, NOT in `CompanySettingsPage.tsx`
 * (decision D5). That page is dead code — `/company-settings` no longer exists and nothing
 * imports it, so a tab added there would be a screen with no route to it.
 *
 * *** NOTHING ON THIS SCREEN IS A PERMISSION CHECK. ***
 *
 * The tab's `visible()` in the registry and the read-only state below are PRESENTATION. S4
 * enforces both the administrative-role check and the whitelabel entitlement server-side, and
 * every write here 403s without them. Do not move a gate out of the controller and into this
 * file.
 *
 * *** THE PREVIEW MUST NOT LEAK INTO THE APP. ***
 *
 * `themeUtils.injectSurfaceVars()` writes `--mg-*` INLINE on `document.documentElement`, which
 * overrides the `.dark-theme`/`.light-theme` stylesheet classes. So a preview built by toggling
 * those classes would not work, and one built by calling `applyThemeClasses`/`applyFullTheme`
 * would reskin the whole application while the drawer is open. The two preview panes therefore
 * carry their own `--mg-*` values as SCOPED inline custom properties on their own container —
 * see `PREVIEW_SURFACE`. Nothing in this file touches `documentElement`.
 */

/** The admin-side record. Distinct from S6's `['public-branding']`, which is the whole app's. */
export const ADMIN_BRANDING_QUERY_KEY = ["admin-branding"] as const;

/**
 * D10 — no SVG. It is an active-content format on an admin-writable path.
 *
 * Derived from `EXTENSION_FORMATS` so the picker's filter, the copy below and the byte
 * sniffing cannot drift apart. S12's contract is the eight names in that map.
 */
const ACCEPTED_EXTENSIONS = Object.keys(EXTENSION_FORMATS);
const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.map((extension) => `.${extension}`).join(",");
/**
 * The list as an administrator reads it — one entry per FORMAT, so `.jpg`/`.jpeg` is one word.
 *
 * Deliberately the same eight the server names. Track B's edge rule answers a rejection with
 * `"The uploaded file was detected as {mime}, which is not a supported image format. Accepted
 * formats: png, jpg, jpeg, webp, ico, heic, heif, avif."` — same set, same order; this copy
 * is the UI register of it, not a second list. Change one and change both.
 */
const ACCEPTED_FORMATS_COPY = "PNG, JPG, WEBP, ICO, HEIC, HEIF or AVIF";
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

const NOT_ENTITLED_MESSAGE =
	"Whitelabelling is not included in your current subscription. Your saved branding is retained and will be restored if the subscription is renewed.";

const FORBIDDEN_MESSAGE = "Your account does not have permission to change this clinic's branding.";

/**
 * The two theme surfaces, as scoped custom properties. Values are copied from
 * `themeUtils.ts`'s `LIGHT_SURFACE` / `DARK_SURFACE` maps — the point of the preview is to
 * answer "will my logo be invisible on the dark sidebar", which is only true if these are the
 * real numbers.
 */
const PREVIEW_SURFACE: Record<"light" | "dark", React.CSSProperties> = {
	light: {
		"--mg-bg": "#f5f5f5",
		"--mg-bg-card": "#ffffff",
		"--mg-border": "rgba(0,0,0,0.08)",
		"--mg-text": "rgba(0,0,0,0.88)",
		"--mg-text-secondary": "rgba(0,0,0,0.48)",
		"--mg-sidebar-bg": "#ffffff",
		"--mg-sidebar-border": "rgba(0,0,0,0.08)",
	} as React.CSSProperties,
	dark: {
		"--mg-bg": "#0a0a0a",
		"--mg-bg-card": "#161616",
		"--mg-border": "rgba(255,255,255,0.08)",
		"--mg-text": "rgba(255,255,255,0.92)",
		"--mg-text-secondary": "rgba(255,255,255,0.50)",
		"--mg-sidebar-bg": "#0d0d0d",
		"--mg-sidebar-border": "rgba(255,255,255,0.06)",
	} as React.CSSProperties,
};

/** What the preview paints when the clinic has chosen no colour — today's app default. */
const PREVIEW_FALLBACK_COLOR = "#1677ff";

const humanBytes = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Client-side file validation — the message the administrator actually reads.
 *
 * Mirrors S4's server rules deliberately rather than relying on them: a 2 MB limit enforced
 * only at the server means uploading a 30 MB photo, waiting for it, and being told no. The
 * server copy remains the real one.
 *
 * ⚠️ **CHECKED BY CONTENT, NOT BY EXTENSION AND NOT BY `file.type` (S12).** S9 checked the
 * extension while the server checked the bytes, so a HEIC named `photo.jpg` — which is what
 * an iPhone hands over — passed here and was then told by the server that it "must be a file
 * of type: png, jpg, jpeg, webp, ico", while being named `.jpg`. Neither the extension nor
 * `file.type` is evidence of anything: `.ico` alone has three MIME spellings in the wild
 * (`image/x-icon`, `image/vnd.microsoft.icon`, and empty), and both are attacker- and
 * camera-controlled. The first sixteen bytes are the only thing here that is not a guess.
 *
 * ⚠️ **A mislabelled but ACCEPTED file passes.** HEIC/HEIF/AVIF are accepted platform-wide
 * now and the file service transcodes them to PNG, so `photo.jpg` containing HEIC uploads
 * fine. The name is not corrected and does not need to be.
 *
 * ⚠️ **SVG stays rejected (D10)** — including one renamed `.png`, which is exactly the case
 * an extension check misses.
 *
 * ⚠️ Async because reading bytes is async. Callers must `await` it.
 */
export const validateBrandingFile = async (file: File): Promise<string | null> => {
	let detected: ImageFormat | null;

	try {
		detected = await detectImageFormat(file);
	} catch {
		// The file became unreadable between the picker and here (moved, unmounted, revoked).
		// FAIL OPEN: this check is message quality, and the server re-sniffs every upload —
		// blocking a legitimate logo over a transient read error would be the worse failure.
		return null;
	}

	if (!isAcceptedFormat(detected)) {
		const claimed = extensionFormat(file.name);
		const tail = `Upload a ${ACCEPTED_FORMATS_COPY} file.`;

		if (detected === null) {
			return `“${file.name}” is not an accepted image type. ${tail}`;
		}

		// Name what was DETECTED — and, when the file name claimed something else, say so.
		// That second half is the whole point: "is a PDF document, not a PNG" is actionable
		// where "must be a file of type: png, jpg, …" on a file called `.png` is not.
		return claimed && claimed !== detected
			? `“${file.name}” is ${FORMAT_DESCRIPTION[detected]}, not a ${FORMAT_LABEL[claimed]}. ${tail}`
			: `“${file.name}” is ${FORMAT_DESCRIPTION[detected]}, which is not an accepted image type. ${tail}`;
	}

	// The cap is on the UPLOADED ORIGINAL (S12 contract). A HEIC that grows past 2 MB as PNG
	// server-side is fine; the file service owns that.
	if (file.size > MAX_UPLOAD_BYTES) {
		return `“${file.name}” is ${humanBytes(file.size)}. The maximum is 2 MB.`;
	}

	return null;
};

/**
 * Turn a write failure into something an administrator can act on.
 *
 * The two 403 bodies S4 sends are machine strings — `branding.not_entitled` and `Forbidden` —
 * and putting either on screen verbatim would read as a crash. Everything else falls through
 * to the repo's shared 422/validation extractor.
 */
export const brandingErrorMessage = (error: unknown): string => {
	const messages = extractValidationErrors(error);

	if (messages.includes("branding.not_entitled")) return NOT_ENTITLED_MESSAGE;
	if (messages.includes("Forbidden")) return FORBIDDEN_MESSAGE;

	return messages.join(" ");
};

// ── Preview ───────────────────────────────────────────────────────────────────────────────

interface BrandPreviewProps {
	theme: "light" | "dark";
	displayName: string;
	color: string | null;
	logoUrl: string | null;
}

/**
 * One forced-theme pane: a miniature login card over a miniature sidebar header, painted with
 * the PENDING (unsaved) name and colour and the CURRENT assets — uploads are immediate, name
 * and colour are not.
 */
const BrandPreview: React.FC<BrandPreviewProps> = ({ theme, displayName, color, logoUrl }) => {
	const accent = color ?? PREVIEW_FALLBACK_COLOR;
	const accentText = getReadableTextColor(accent);
	const name = displayName.trim() || "Your clinic";

	/**
	 * A FACTORY, not a shared element — the lockup appears twice per pane and two nodes
	 * carrying the same `data-testid` make every `getByTestId` in a test ambiguous.
	 */
	const logo = (slot: "card" | "sidebar") =>
		logoUrl ? (
			<img
				src={logoUrl}
				alt=""
				style={{ height: slot === "card" ? 28 : 20, width: "auto", maxWidth: "100%" }}
				data-testid={`brand-preview-${slot}-logo-${theme}`}
			/>
		) : (
			<XoeMedLogo height={20} />
		);

	return (
		<div
			data-testid={`brand-preview-${theme}`}
			style={{
				...PREVIEW_SURFACE[theme],
				flex: "1 1 240px",
				minWidth: 240,
				background: "var(--mg-bg)",
				color: "var(--mg-text)",
				border: "1px solid var(--mg-border)",
				borderRadius: 8,
				padding: 12,
				display: "flex",
				flexDirection: "column",
				gap: 10,
			}}
		>
			<span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--mg-text-secondary)" }}>
				{theme === "dark" ? "Dark theme" : "Light theme"}
			</span>

			{/* Miniature login card */}
			<div
				style={{
					background: "var(--mg-bg-card)",
					border: "1px solid var(--mg-border)",
					borderRadius: 6,
					padding: 12,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 8,
				}}
			>
				{logo("card")}
				<span style={{ fontSize: 12, color: "var(--mg-text)" }}>{name}</span>
				<span
					style={{
						display: "inline-block",
						background: accent,
						color: accentText,
						borderRadius: 4,
						fontSize: 11,
						padding: "4px 14px",
					}}
				>
					Sign in
				</span>
			</div>

			{/* Miniature sidebar header */}
			<div
				style={{
					background: "var(--mg-sidebar-bg)",
					border: "1px solid var(--mg-sidebar-border)",
					borderRadius: 6,
					padding: "8px 10px",
					display: "flex",
					alignItems: "center",
					gap: 8,
				}}
			>
				{logo("sidebar")}
				<span style={{ marginLeft: "auto", width: 44, height: 6, borderRadius: 3, background: accent }} />
			</div>
		</div>
	);
};

// ── Asset slot ────────────────────────────────────────────────────────────────────────────

interface AssetSlotProps {
	variant: BrandingAssetVariant;
	title: string;
	description: string;
	asset: BrandingAsset;
	disabled: boolean;
	busy: boolean;
	onSelect: (variant: BrandingAssetVariant, file: File) => void;
	onRemove: (variant: BrandingAssetVariant) => void;
	/** Painted behind the thumbnail so a white-on-white or black-on-black logo is visible. */
	swatch: string;
}

const AssetSlot: React.FC<AssetSlotProps> = ({
	variant,
	title,
	description,
	asset,
	disabled,
	busy,
	onSelect,
	onRemove,
	swatch,
}) => (
	<div
		data-testid={`branding-slot-${variant}`}
		style={{
			display: "flex",
			alignItems: "center",
			gap: 12,
			padding: "10px 0",
			borderBottom: "1px solid var(--mg-border)",
			flexWrap: "wrap",
		}}
	>
		<div style={{ minWidth: 160, flex: "0 0 auto" }}>
			<div style={{ fontSize: 13, fontWeight: 600, color: "var(--mg-text)" }}>{title}</div>
			<Text type="secondary" style={{ fontSize: 11 }}>
				{description}
			</Text>
		</div>

		<div
			style={{
				width: 120,
				height: 44,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: swatch,
				border: "1px solid var(--mg-border)",
				borderRadius: 4,
				overflow: "hidden",
			}}
		>
			{asset.url ? (
				<img
					src={asset.url}
					alt={`${title} preview`}
					data-testid={`branding-asset-${variant}`}
					style={{ maxHeight: 36, maxWidth: 108, width: "auto" }}
				/>
			) : (
				<Text type="secondary" style={{ fontSize: 11 }}>
					Not set
				</Text>
			)}
		</div>

		<Space>
			<Upload
				accept={ACCEPT_ATTRIBUTE}
				showUploadList={false}
				disabled={disabled || busy}
				beforeUpload={(file) => {
					onSelect(variant, file as unknown as File);
					// Always false — this repo POSTs manually through the shared client so the request
					// carries the session and the `variant` field.
					return false;
				}}
			>
				<Button size="small" icon={<UploadOutlined />} disabled={disabled || busy} loading={busy} data-testid={`branding-upload-${variant}`}>
					{asset.url ? "Replace" : "Upload"}
				</Button>
			</Upload>

			{asset.url && (
				<Button
					size="small"
					danger
					icon={<DeleteOutlined />}
					disabled={disabled || busy}
					onClick={() => onRemove(variant)}
					data-testid={`branding-remove-${variant}`}
				>
					Remove
				</Button>
			)}
		</Space>
	</div>
);

// ── Screen ────────────────────────────────────────────────────────────────────────────────

const BrandingConfiguration: React.FC = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotifications();
	// S6. Called after every successful write so the sidebar, the browser title and the brand
	// colour update live — the settings drawer opens over the app it is re-branding.
	const { refresh } = useBranding();

	const { data, isPending, isError } = useQuery({
		queryKey: ADMIN_BRANDING_QUERY_KEY,
		queryFn: fetchBrandingAdmin,
		retry: false,
	});

	const [displayName, setDisplayName] = useState("");
	const [primaryColor, setPrimaryColor] = useState<string | null>(null);
	const [writeError, setWriteError] = useState<string | null>(null);
	const [busyVariant, setBusyVariant] = useState<BrandingAssetVariant | null>(null);

	/**
	 * Seed the pending fields from the stored record.
	 *
	 * ⚠️ Keyed on the VALUES, not on `data`. A background refetch that returns an unchanged
	 * record produces a new object identity every time, and depending on `data` would wipe
	 * whatever the administrator had typed since the screen opened.
	 */
	useEffect(() => {
		setDisplayName(data?.display_name ?? "");
		setPrimaryColor(data?.primary_color ?? null);
	}, [data?.display_name, data?.primary_color]);

	const entitled = data?.entitled === true;
	const readOnly = !entitled;

	const applyRecord = (record: BrandingAdmin) => {
		queryClient.setQueryData(ADMIN_BRANDING_QUERY_KEY, record);
		void queryClient.invalidateQueries({ queryKey: ADMIN_BRANDING_QUERY_KEY });
		refresh();
		setWriteError(null);
	};

	const handleWriteFailure = (error: unknown) => {
		const message = brandingErrorMessage(error);
		setWriteError(message);
		showError(message);
	};

	const saveMutation = useMutation({
		mutationFn: (payload: UpdateBrandingPayload) => updateBranding(payload),
		onSuccess: (record) => {
			applyRecord(record);
			showSuccess("Branding saved");
		},
		onError: handleWriteFailure,
	});

	const uploadMutation = useMutation({
		mutationFn: ({ variant, file }: { variant: BrandingAssetVariant; file: File }) =>
			uploadBrandingAsset(variant, file),
		onSuccess: (record) => {
			applyRecord(record);
			showSuccess("Image uploaded");
		},
		onError: handleWriteFailure,
		onSettled: () => setBusyVariant(null),
	});

	const removeMutation = useMutation({
		mutationFn: (variant: BrandingAssetVariant) => deleteBrandingAsset(variant),
		onSuccess: () => {
			// DELETE answers 204 with no body, so there is nothing to seed state from — refetch.
			void queryClient.invalidateQueries({ queryKey: ADMIN_BRANDING_QUERY_KEY });
			refresh();
			setWriteError(null);
			showSuccess("Image removed");
		},
		onError: handleWriteFailure,
		onSettled: () => setBusyVariant(null),
	});

	const handleSave = () => {
		// Both keys are always sent because the form holds both and the administrator is
		// looking at both. `null` clears; S4 leaves an ABSENT key unchanged, which is the
		// behaviour that makes a partial payload safe — it is simply not what this form means.
		saveMutation.mutate({
			brand_display_name: displayName.trim() === "" ? null : displayName.trim(),
			brand_primary_color: primaryColor,
		});
	};

	// ⚠️ Async since S12 — `validateBrandingFile` reads the file's leading bytes. `beforeUpload`
	// ignores the returned promise (it always returns `false`; this repo POSTs manually), so the
	// only thing that changes for the caller is that the rejection lands a tick later.
	const handleSelect = async (variant: BrandingAssetVariant, file: File) => {
		const problem = await validateBrandingFile(file);
		if (problem) {
			// Rejected BEFORE any network call — no request is made for a .svg, a .pdf or an
			// oversized image.
			setWriteError(problem);
			showError(problem);
			return;
		}

		setWriteError(null);
		setBusyVariant(variant);
		uploadMutation.mutate({ variant, file });
	};

	const handleRemove = (variant: BrandingAssetVariant) => {
		setBusyVariant(variant);
		removeMutation.mutate(variant);
	};

	/**
	 * D12 — WARN, never block. This drives an `Alert`; the Save button never reads it.
	 *
	 * 🚨 **EVERY colour fails one of the two surfaces — see `failingContrasts` in
	 * `contrast.ts` for the arithmetic.** So BOTH results are rendered, passing one included:
	 * an alert that only ever said "your colour is bad" would be noise, while "5.47:1 on
	 * light, 3.31:1 on dark" tells an administrator which theme to look at. Never turn this
	 * into a `disabled` — it would refuse every colour a clinic could choose.
	 */
	const contrast = useMemo(() => checkBrandColorContrast(primaryColor), [primaryColor]);
	const failures = contrast.filter((result) => !result.passes);

	if (isPending) {
		return (
			<div style={{ padding: 24, textAlign: "center" }}>
				<Spin data-testid="branding-loading" />
			</div>
		);
	}

	if (isError || !data) {
		return (
			<Alert
				type="error"
				showIcon
				message="Unable to load branding settings"
				description="Reload the page to try again. If this keeps happening, your account may not have permission to manage clinic branding."
				data-testid="branding-load-error"
			/>
		);
	}

	const darkPreviewLogo = data.logo_dark.url ?? data.logo.url;

	return (
		<Space direction="vertical" size={16} style={{ width: "100%" }}>
			{readOnly && (
				<Alert
					type="info"
					showIcon
					message="Whitelabelling is not active"
					description={NOT_ENTITLED_MESSAGE}
					data-testid="branding-not-entitled"
				/>
			)}

			{writeError && (
				<Alert type="error" showIcon message={writeError} closable onClose={() => setWriteError(null)} data-testid="branding-write-error" />
			)}

			<SectionCard title="Clinic identity" subtitle="Shown on the login page, the staff shell, the patient portal and the public booking form.">
				<Space direction="vertical" size={16} style={{ width: "100%" }}>
					<div>
						<div style={{ fontSize: 12, marginBottom: 4, color: "var(--mg-text)" }}>Clinic name</div>
						<Input
							value={displayName}
							maxLength={120}
							showCount
							disabled={readOnly}
							placeholder="Your clinic's name"
							onChange={(e) => setDisplayName(e.target.value)}
							data-testid="branding-display-name"
						/>
					</div>

					<div>
						<div style={{ fontSize: 12, marginBottom: 4, color: "var(--mg-text)" }}>Brand colour</div>
						<Space>
							<ColorPicker
								format="hex"
								disabledAlpha
								disabled={readOnly}
								value={primaryColor ?? undefined}
								// S4 rejects anything that is not `#RRGGBB` — six digits, no shorthand,
								// no alpha — because the value is written into a CSS custom property in
								// S7 and a permissive validator there is an injection surface.
								onChange={(value) => setPrimaryColor(value.toHexString().slice(0, 7).toLowerCase())}
								showText
							/>
							<Button
								size="small"
								disabled={readOnly || !primaryColor}
								onClick={() => setPrimaryColor(null)}
								data-testid="branding-clear-color"
							>
								Clear
							</Button>
							<Text type="secondary" style={{ fontSize: 11 }}>
								{primaryColor ?? "Using the Xoe Med palette"}
							</Text>
						</Space>
					</div>

					{failures.length > 0 && (
						<Alert
							type="warning"
							showIcon
							data-testid="branding-contrast-warning"
							message={`Contrast check — below WCAG AA (${WCAG_AA_NORMAL}:1) on the ${failures
								.map((failure) => failure.theme)
								.join(" and ")} theme`}
							description={
								<>
									{contrast.map((result) => (
										<div key={result.theme}>
											{`${result.theme === "dark" ? "Dark" : "Light"} theme surface (${result.surface}): ${formatRatio(
												result.ratio,
											)} — ${result.passes ? "passes AA" : "below AA"}.`}
										</div>
									))}
									<Paragraph type="secondary" style={{ fontSize: 11, marginBottom: 0, marginTop: 4 }}>
										Small text in this colour may be hard to read on that theme. You can save it anyway.
									</Paragraph>
								</>
							}
						/>
					)}

					<div>
						<Button
							type="primary"
							icon={<SaveOutlined />}
							loading={saveMutation.isPending}
							// ⚠️ D12 — `failures` is DELIBERATELY absent from this expression. A
							// failing-contrast colour warns; it never blocks the save.
							disabled={readOnly}
							onClick={handleSave}
							data-testid="branding-save"
						>
							Save
						</Button>
					</div>
				</Space>
			</SectionCard>

			<SectionCard
				title="Images"
				subtitle={`${ACCEPTED_FORMATS_COPY}, up to 2 MB each. HEIC, HEIF and AVIF are converted to PNG when they are saved.`}
			>
				<AssetSlot
					variant="logo"
					title="Logo"
					description="Used everywhere unless a dark version is set."
					asset={data.logo}
					disabled={readOnly}
					busy={busyVariant === "logo"}
					onSelect={handleSelect}
					onRemove={handleRemove}
					swatch="#ffffff"
				/>
				<AssetSlot
					variant="logo_dark"
					title="Dark logo (optional)"
					description="Falls back to the logo above when not set."
					asset={data.logo_dark}
					disabled={readOnly}
					busy={busyVariant === "logo_dark"}
					onSelect={handleSelect}
					onRemove={handleRemove}
					swatch="#0d0d0d"
				/>
				<AssetSlot
					variant="favicon"
					title="Favicon"
					description="The small icon in the browser tab."
					asset={data.favicon}
					disabled={readOnly}
					busy={busyVariant === "favicon"}
					onSelect={handleSelect}
					onRemove={handleRemove}
					swatch="#ffffff"
				/>
				<Text type="secondary" style={{ fontSize: 11, display: "block", paddingTop: 8 }}>
					Browsers cache favicons aggressively — a new one may need a hard reload to appear.
				</Text>
			</SectionCard>

			<SectionCard title="Preview" subtitle="Your unsaved name and colour, on both themes.">
				<div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
					<BrandPreview theme="light" displayName={displayName} color={primaryColor} logoUrl={data.logo.url} />
					<BrandPreview theme="dark" displayName={displayName} color={primaryColor} logoUrl={darkPreviewLogo} />
				</div>
			</SectionCard>
		</Space>
	);
};

export default BrandingConfiguration;
